import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import { ActKc3LevelEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useUerContractObjects } from 'pages/Project/UerProject/Contract/ContractStructure/hooks/useUerContractObjects';
import { useUerContractWorkOrders } from 'pages/Project/UerProject/Contract/hooks/useUerContractWorkOrders';
import { Text } from 'uikit';
import { formatDateToLocale } from 'utils';

import type { RowClassParams } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import type { CSSProperties } from 'react';
import { useMemo, forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';

import { ActKC3Totals } from '../ActKC3Totals';

import { useActKC3ListColumns, useActKC3ListData } from './hooks';
import { useActKC3ListDeleteItems } from './hooks/useActKC3ListDeleteItems';
import type { ActListItemWithGroupInfo } from './types';

import Styles from './actKC3ListTable.module.css';

interface IActKC3ListTable {
    contractId: number;
    costRounding: number;
    actKC3Level?: ActKc3LevelEnum;
    contractUniqueId?: string | null;
    onActKc3Add: (data: ActListItemWithGroupInfo) => void;
}

const kc3ListClassRules: Record<string, (params: RowClassParams<ActListItemWithGroupInfo>) => boolean> = {
    isTopLevelGroup: (params) => !!params?.data?.isGroup && params?.data?.level === 0,
    isGroup: (params) => !!params?.data?.isGroup,
    usualRow: (params) => !params?.data?.isGroup && !params?.data?.actKC3IsInactive,
    isInactiveAct: (params) => !!params?.data?.actKC3IsInactive,
};

export const ActKC3ListTable = forwardRef((props: IActKC3ListTable, ref) => {
    const { contractId, costRounding, actKC3Level, contractUniqueId, onActKc3Add } = props;
    const tableSettingsRef = useRef<HTMLDivElement | null>(null);
    const listTableRef = useRef<AgGridReact<ActListItemWithGroupInfo>>(null);

    const [tableStartPosition, setTableStartPosition] = useState<number | undefined>();

    useEffect(() => {
        if (tableSettingsRef.current) {
            setTableStartPosition(tableSettingsRef?.current?.getBoundingClientRect()?.bottom);
        }
    }, [tableSettingsRef]);

    const { items: workOrdersItems } = useUerContractWorkOrders(
        contractId,
        !!(contractUniqueId && actKC3Level === ActKc3LevelEnum.WorkOrder),
    );

    const { uerContractObjectsData } = useUerContractObjects(
        contractId,
        !!(contractId && actKC3Level === ActKc3LevelEnum.Object),
    );

    const levelItems = useMemo(() => {
        if (actKC3Level === ActKc3LevelEnum.WorkOrder) {
            return workOrdersItems
                ?.filter((w) => w.status !== WorkOrderStatusEnum.OnFormation && w.hasActsKC2)
                ?.map((w) => ({
                    id: w.workOrderId,
                    name: `НЗ №${w.internalNumber} от ${formatDateToLocale(w.signingDate)}`,
                    status: w.status,
                }));
        }

        if (actKC3Level === ActKc3LevelEnum.Object) {
            return uerContractObjectsData?.uerContractObjects
                ?.filter((o) => !o.parentId && o.hasActsKC2)
                .map((c) => ({
                    id: c.id,
                    name: c.name,
                    status: c.status,
                    hasWorkOrdersInProgress: c.hasWorkOrdersInProgress,
                }));
        }

        return [{ id: contractId, name: 'Договор' }];
    }, [actKC3Level, contractId, uerContractObjectsData?.uerContractObjects, workOrdersItems]);

    const { items, itemsCount, changeCollapseState, collapseState, changeFilterHandler, refreshListActListData } =
        useActKC3ListData({ contractId, tableApi: listTableRef?.current?.api, actKC3Level, levelItems });

    const {
        columns,
        columnsForSettings,
        handleTableSettingsConfirm,
        isColumnVisible,
        checkedRowsArr,
        resetCheckedRowsHandler,
        singleDeleteItemData,
        kc3ForTotalsData,
        closeKc3Totals,
    } = useActKC3ListColumns({
        contractId,
        changeCollapseState,
        collapseState,
        changeFilterHandler,
        costRounding,
        refreshListActListData,
        onActKc3Add,
        actKC3Level,
    });

    const { deleteData, handleDelete, deleteErr, deleteSetData, deleteStatus } = useActKC3ListDeleteItems({
        contractId,
        checkedIds: singleDeleteItemData ? singleDeleteItemData?.actKC3Id : checkedRowsArr,
        refreshTable: refreshListActListData,
        resetChecked: resetCheckedRowsHandler,
    });

    useImperativeHandle(
        ref,
        () => ({
            api: listTableRef?.current,
            resetCheckedRows: resetCheckedRowsHandler,
        }),
        [resetCheckedRowsHandler],
    );

    return (
        <>
            <TableTopPanel
                ref={tableSettingsRef}
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRowsHandler}
                totalItems={itemsCount}
                fetchedItems={itemsCount}
                optionalColumns={columnsForSettings}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                areChildrenOnLeftSide
                showPinColumn
                tableApi={listTableRef?.current}
                leftSide={
                    <DeleteItemFrom
                        modalTextHeader="Удаление справки кс-3"
                        modalTextConfirmation={
                            <Text style={{ padding: '0 30px 0 24px' }}>
                                Вы уверены, что хотите удалить Справку КС-3
                                {singleDeleteItemData?.actKC2FullNumber
                                    ? ` №${singleDeleteItemData?.actKC2FullNumber}`
                                    : ''}{' '}
                                {singleDeleteItemData?.actKC3Date ? `от ${singleDeleteItemData?.actKC3Date}` : ''}?
                            </Text>
                        }
                        isOpened={Boolean(singleDeleteItemData)}
                        closeDeleteModal={resetCheckedRowsHandler}
                        deleteData={deleteData}
                        deleteStatus={deleteStatus}
                        deleteError={deleteErr}
                        deleteSetData={deleteSetData}
                        handleDelete={handleDelete}
                    />
                }
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteErr}
                    deleteSetData={deleteSetData}
                    handleDelete={handleDelete}
                    style={{ marginRight: 10 }}
                />
            </TableTopPanel>

            <div
                className={cn('ag-theme-alpine', Styles.actList)}
                style={
                    {
                        '--tableStartPosition': `${tableStartPosition ?? 0}px`,
                    } as CSSProperties
                }
            >
                <AgGridTable
                    ref={listTableRef}
                    columnDefs={columns}
                    rowData={items}
                    overlayNoRowsTemplate="Для отображения данных сформируйте Справку КС-3"
                    rowClassRules={kc3ListClassRules}
                />
            </div>

            {kc3ForTotalsData && <ActKC3Totals onClose={closeKc3Totals} kc3ForTotalsData={kc3ForTotalsData} />}
        </>
    );
});

ActKC3ListTable.displayName = 'ActKC3ListTable';
